import { Permission } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
import { registerClientTools } from './tools';
function summarize(g, controllers) {
    return {
        id: g.deviceFingerprint,
        name: g.displayName || g.hostname || g.deviceFingerprint,
        status: g.status,
        location: g.geo || '',
        kind: g.nodeKind || '',
        // What answers "which relays need an update" without a second call. '' when the box has not
        // reported one — absent, never guessed.
        version: g.softwareVersion || '',
        /*
         * Present only when the surface can answer it, and `[]` is a real answer — "this box has no
         * controllers" is what stops an agent trying it. `undefined` means "this surface does not know",
         * which is a different sentence and must not read as an empty plant.
         */
        ...(controllers ? { controllers } : {}),
    };
}
/** The one-line-per-gateway summary the model reads first. Controllers inline, because the whole
 *  point is that "which box is that controller on" costs no second call. */
function line(r) {
    const c = r.controllers;
    if (!c)
        return `${r.name} (${r.status})`;
    return `${r.name} (${r.status}) — ${c.length ? `controllers: ${c.map((x) => x.name).join(', ')}` : 'no controllers'}`;
}
export function registerGatewayTools(deps) {
    const list = {
        name: 'gateways.list',
        permission: Permission.GATEWAY_VIEW,
        description: "List every relay this organization owns — id, name, whether it's online, and WHICH CONTROLLERS are on each " +
            'one. This is the FIRST call to make in an unfamiliar org, and the call that tells you where a controller ' +
            'lives: every other tool takes a relay AND a controller, and naming a controller against the wrong relay ' +
            'is the most common way a call fails. One call here orients you completely. Do not guess an id, and do not ' +
            'search memory or old conversations for one — this always has the current, complete list.',
        mutates: false,
        parameters: { type: 'object', properties: {} },
        async run() {
            const rows = deps.entries().map((e) => summarize(e.gateway, deps.controllersOf?.(e)));
            if (rows.length === 0) {
                return { ok: true, message: 'This organization has no relays yet.', data: { gateways: [] } };
            }
            return {
                ok: true,
                message: `${rows.length} gateway(s). ${rows.map(line).join('; ')}.`,
                data: { gateways: rows },
            };
        },
    };
    const search = {
        name: 'gateways.search',
        permission: Permission.GATEWAY_VIEW,
        description: 'Find a relay by (partial, case-insensitive) name — "max", "cloud", "nuremberg" all match. Use this when a person ' +
            'names a relay loosely rather than by its exact display name. Returns the same id/name/status shape as gateways.list.',
        mutates: false,
        parameters: {
            type: 'object',
            properties: { query: { type: 'string', description: 'Part of the name to match, e.g. "max" or "cloud".' } },
            required: ['query'],
        },
        async run(input) {
            const q = typeof input.query === 'string' ? input.query.trim().toLowerCase() : '';
            if (!q)
                return { ok: false, message: 'query is required — give at least part of a name.' };
            const rows = deps
                .entries()
                .filter((e) => (e.gateway.displayName || e.gateway.hostname || e.gateway.deviceFingerprint).toLowerCase().includes(q))
                .map((e) => summarize(e.gateway, deps.controllersOf?.(e)));
            if (rows.length === 0) {
                return {
                    ok: true,
                    message: `No relay name matches "${q}". Try gateways.list to see everything this org has.`,
                    data: { gateways: [] },
                };
            }
            return { ok: true, message: `${rows.length} match(es) for "${q}". ${rows.map(line).join('; ')}.`, data: { gateways: rows } };
        },
    };
    return registerClientTools(list, search);
}
//# sourceMappingURL=gateway-tools.js.map